import React, { useContext } from 'react';
import * as _ from 'lodash';
import { Accordion, Card, useAccordionToggle, AccordionContext } from 'react-bootstrap';
import { ChargeDescription, PaymentAmount } from 'guest-data-types-ts-lib';
import './CancellationCharges.scss';
import { Icon } from '@vacasa/react-components-lib';
import { CurrencyUtils } from '../../utils';

const AccordionHeader: React.FC<{ title: string; amount: PaymentAmount; eventKey: string }> = (props) => {
    const { title, amount, eventKey } = props;

    const currentEventKey = useContext(AccordionContext);
    const decoratedOnClick = useAccordionToggle(eventKey);

    const isCurrentEventKey = currentEventKey === eventKey;

    return (
        <div onClick={decoratedOnClick} className="charges-accordion-header">
            <div className="body-regular charges-accordion-header-title">
                {title} {isCurrentEventKey ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
            </div>
            <div className="body-bold charges-accordion-header-amount">{CurrencyUtils.formatCurrency(amount.total, amount.currency)}</div>
        </div>
    );
};

const AccordionItem: React.FC<{ description: string; amount: PaymentAmount }> = (props) => {
    const { description, amount } = props;
    return (
        <div className="charges-accordion-item">
            <div className="body-regular charges-accordion-item-description">{description}</div>
            <div className="body-bold charges-accordion-item-amount">{CurrencyUtils.formatCurrency(amount.total, amount.currency)}</div>
        </div>
    );
};

interface CancellationChargesProps {
    charges: ChargeDescription;
    className?: string;
}

export const CancellationCharges: React.FC<CancellationChargesProps> = (props) => {
    const {
        charges: { total, fees, taxes, rent, booked_currency },
        className,
    } = props;

    const totalFees = _.reduce(fees, (acc, curr) => +curr.amount + acc, 0);
    const totalTaxes = _.reduce(taxes, (acc, curr) => +curr.amount + acc, 0);
    const totalRent = _.reduce(rent, (acc, curr) => +curr.rent + acc, 0);

    return (
        <div className={`cancellation-charges ${className ?? ''}`}>
            <p className="body-xl cancellation-charges-title">Cancellation Charges</p>
            <Accordion>
                {fees.length > 0 && totalFees > 0 && (
                    <React.Fragment>
                        <AccordionHeader title={'Fees'} eventKey={'fees'} amount={{ total: totalFees, currency: booked_currency }} />
                        <Accordion.Collapse eventKey="fees">
                            <div>
                                {fees.map((fee, i) => {
                                    return (
                                        +fee.amount > 0 && (
                                            <AccordionItem
                                                key={i}
                                                description={fee.name}
                                                amount={{ total: +fee.amount, currency: booked_currency }}
                                            />
                                        )
                                    );
                                })}
                            </div>
                        </Accordion.Collapse>
                    </React.Fragment>
                )}

                {taxes.length > 0 && totalTaxes > 0 && (
                    <React.Fragment>
                        <AccordionHeader title={'Taxes'} eventKey={'taxes'} amount={{ total: totalTaxes, currency: booked_currency }} />
                        <Accordion.Collapse eventKey="taxes">
                            <div>
                                {taxes.map((tax, i) => {
                                    return (
                                        +tax.amount > 0 && (
                                            <AccordionItem
                                                key={i}
                                                description={tax.name}
                                                amount={{ total: +tax.amount, currency: booked_currency }}
                                            />
                                        )
                                    );
                                })}
                            </div>
                        </Accordion.Collapse>
                    </React.Fragment>
                )}

                {rent.length > 0 && totalRent > 0 && (
                    <React.Fragment>
                        <AccordionHeader title={'Rent'} eventKey={'rent'} amount={{ total: totalRent, currency: booked_currency }} />
                        <Accordion.Collapse eventKey="rent">
                            <Card.Body>
                                {rent.map((day, i) => {
                                    return (
                                        +day.rent > 0 && (
                                            <AccordionItem
                                                key={i}
                                                description={day.date}
                                                amount={{ total: +day.rent, currency: booked_currency }}
                                            />
                                        )
                                    );
                                })}
                            </Card.Body>
                        </Accordion.Collapse>
                    </React.Fragment>
                )}
            </Accordion>

            <hr className="charges-divider" />

            <div className="body-bold charges-details-total">{CurrencyUtils.formatCurrency(+total, booked_currency)}</div>
        </div>
    );
};
